'use client'

/**
 * Loads Google Analytics 4 only after the user has granted analytics
 * consent via the cookie banner. Reads `ps_cookie_consent` from
 * localStorage and listens for `ps:consent` events dispatched by the
 * banner to react in the same tab.
 *
 * NB-F192 (Subresource Integrity) policy
 * --------------------------------------
 * gtag.js is the ONLY external <script src=...> PulseSignal embeds;
 * everything else (Stripe Checkout, Sentry, PostHog) ships via the npm
 * SDKs from our own origin where SRI does not apply.  Google rotates
 * gtag.js content on their own deploy cadence, so pinning an `integrity`
 * hash on it would silently break analytics on every Google deploy
 * with no notification path back to us.  Google's own guidance is to
 * NOT pin SRI hashes on gtag (see
 * https://developers.google.com/tag-platform/security and
 * https://web.dev/articles/sri).  We compensate with three layers:
 *   - Hard-gate the load behind explicit consent (consent === 'all' |
 *     'analytics'); a tampered gtag never executes for declined users.
 *   - The CSP header (NB-F187) restricts script-src to
 *     googletagmanager.com so an injected script from a hostile CDN
 *     cannot take its place.
 *   - The Permissions-Policy (NB-F191) denies the sensors gtag could
 *     abuse if compromised (geolocation, camera, microphone).
 * When PulseSignal adds a self-hosted external script (e.g. a versioned
 * widget on a stable CDN), SRI MUST be applied via `integrity="sha384-…"`
 * + `crossorigin="anonymous"` on the <Script> tag.
 */

import Script from 'next/script'
import { useEffect, useState } from 'react'
import { readConsent } from './CookieBanner'

const GA_MEASUREMENT_ID = 'G-0H2D8NMSFJ'

export default function Analytics() {
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    const sync = () => {
      const v = readConsent()
      setEnabled(v === 'all' || v === 'analytics')
    }
    sync()
    const handler = () => sync()
    window.addEventListener('ps:consent', handler)
    window.addEventListener('storage', handler)
    return () => {
      window.removeEventListener('ps:consent', handler)
      window.removeEventListener('storage', handler)
    }
  }, [])

  if (!enabled) return null

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} strategy="afterInteractive" />
      <Script id="gtag-init" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${GA_MEASUREMENT_ID}', { anonymize_ip: true });`}
      </Script>
    </>
  )
}
